import React from "react";
import Select from "react-select";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {nombreVendedor} from "../../_helpers";
import tiendaService from "../../_services/cart/tienda.service";

import CargadorVersiones from "../../_helpers/CargadorVersiones";

const CONDICIONES = [
    {id: "NUEVO", nombre: "Nuevos"},
    {id: "USADO", nombre: "Usados"},
];

/**
 * Lo que se limpia junto con cada filtro: marca, modelo y version son una cadena, y un modelo
 * de otra marca dejaria el listado vacio sin que se vea por que.
 */
const DEPENDIENTES = {
    marca: ["modelo", "version"],
    modelo: ["version"],
};

/** Orden en que se muestran los chips: el mismo que el panel. */
const CAMPOS = ["categoria", "tipoRepuesto", "marca", "modelo", "version", "condicion", "provincia", "vendedor"];

const sinSeparador = {IndicatorSeparator: () => null};

const customSelect = {
    control: (provided) => ({
        ...provided,
        background: "transparent",
        borderColor: "transparent",
        boxShadow: "none",
    }),
    menu: (provided) => ({...provided, zIndex: 20}),
};

const porId = (lista, id) => (id === undefined ? null : (lista || []).find((it) => it.id === id) || null);

/**
 * Los filtros del listado, agrupados en "Repuesto", "Vehiculo" y "Mas".
 *
 * <p>Antes eran hasta ocho combos iguales uno abajo del otro, sin rotulo, y Modelo/Version
 * se veian habilitados sin marca (se les pasaba `disabled`, que react-select ignora: el prop
 * es `isDisabled`).
 */
class MenuFiltro extends React.Component {

    buscarMisModelos = (modelos, marcaId) =>
        marcaId === undefined ? [] : (modelos || []).filter((it) => it.marca.id === marcaId);

    buscarMisVersiones = (versiones, modeloId) =>
        modeloId === undefined ? [] : (versiones || []).filter((it) => it.modelo.id === modeloId);

    ordenarVendedores = (vendedores) =>
        (vendedores ?? [])
            .slice()
            .sort((a, b) => nombreVendedor(a).localeCompare(nombreVendedor(b)));

    /**
     * Aplica un filtro apenas se elige.
     *
     * <p>El filtro siempre se aplico en el onChange; el boton "Aplicar filtros" que
     * habia en mobile no aplicaba nada, solo cerraba el panel — y hasta que no lo
     * tocabas tapaba el listado que ya se habia actualizado atras. Ahora el panel se
     * cierra solo: el que lo abrio avisa como cerrarlo con `alAplicar`.
     *
     * <p>En desktop el panel no es un offcanvas sino una columna fija, asi que
     * `alAplicar` no llega y no hay nada que cerrar.
     *
     * <p>Elegir marca no cierra el panel: lo siguiente casi siempre es el modelo.
     */
    aplicar = (campo, valor) => {
        const {setFieldValue, submitForm, alAplicar} = this.props;

        setFieldValue(campo, valor || undefined);
        (DEPENDIENTES[campo] || []).forEach((dep) => setFieldValue(dep, undefined));
        submitForm();

        if (alAplicar && !(campo === "marca" && valor)) alAplicar();
    };

    /**
     * Elegir una categoria acota el combo de tipos; si el tipo elegido no es de esa categoria se
     * limpia, para no quedar filtrando por algo que ya no se ve en el combo.
     */
    aplicarCategoria = (opt) => {
        const {setFieldValue, submitForm, alAplicar, values, filtros} = this.props;
        const tipo = (filtros?.tipoRepuestos || []).find((t) => t.id === values.tipoRepuesto?.id);

        setFieldValue("categoria", opt || undefined);
        if (opt && tipo && tipo.categoriaId !== opt.id) setFieldValue("tipoRepuesto", undefined);
        submitForm();

        if (alAplicar) alAplicar();
    };

    /** Los tipos de la categoria elegida, o todos. */
    tiposDeCategoria = (tipos, categoriaId) =>
        categoriaId ? (tipos || []).filter((t) => t.categoriaId === categoriaId) : tipos;

    /** Un combo con su rotulo; el rotulo apunta al input de react-select para que se lea. */
    campo = (id, rotulo, props) => (
        <div className="filtros__campo">
            <label htmlFor={"filtro-" + id}>{rotulo}</label>
            <Select
                inputId={"filtro-" + id}
                className={"sideBar-select" + (props.isDisabled ? " sideBar-select--espera" : "")}
                isClearable={true}
                getOptionLabel={(option) => `${option.nombre}`}
                getOptionValue={(option) => option.id}
                components={sinSeparador}
                styles={customSelect}
                {...props}
            />
        </div>
    );

    render() {
        /*
         * `ocultarTipoRepuesto`: en el listado de bajas el tipo ya esta fijado a "Baja" por la
         * propia pantalla, asi que ofrecer el combo solo serviria para salirse de ella.
         */
        const {filtros, values, ocultarTipoRepuesto} = this.props;
        const enTienda = tiendaService.esTienda();
        const hayCategorias = (filtros?.categorias || []).length > 0;

        return (
            <div className="filtros">
                {!ocultarTipoRepuesto && (
                    <fieldset className="filtros__grupo">
                        <legend className="filtros__titulo">Repuesto</legend>

                        {hayCategorias && this.campo("categoria", "Categoría", {
                            value: porId(filtros.categorias, values.categoria?.id),
                            placeholder: "Todas",
                            options: filtros.categorias,
                            onChange: this.aplicarCategoria,
                        })}

                        {/* null y no undefined en los value: con undefined react-select sigue
                            mostrando lo ultimo elegido aunque el valor se haya limpiado. */}
                        {this.campo("tipo", "Tipo de repuesto", {
                            value: porId(filtros?.tipoRepuestos, values.tipoRepuesto?.id),
                            placeholder: "Todos",
                            options: this.tiposDeCategoria(filtros?.tipoRepuestos, values.categoria?.id),
                            noOptionsMessage: () => "No se encontraron repuestos",
                            onChange: (opt) => this.aplicar("tipoRepuesto", opt),
                        })}
                    </fieldset>
                )}

                <fieldset className="filtros__grupo">
                    <legend className="filtros__titulo">Vehículo</legend>

                    {this.campo("marca", "Marca", {
                        value: porId(filtros?.marcas, values.marca?.id),
                        placeholder: "Todas",
                        options: filtros?.marcas,
                        noOptionsMessage: () => "No se encontraron marcas",
                        onChange: (opt) => this.aplicar("marca", opt),
                    })}

                    {this.campo("modelo", "Modelo", {
                        isDisabled: values.marca?.id === undefined,
                        value: porId(filtros?.modelos, values.modelo?.id),
                        placeholder: values.marca?.id === undefined ? "Elegí una marca" : "Todos",
                        options: this.buscarMisModelos(filtros?.modelos, values.marca?.id),
                        noOptionsMessage: () => "No se encontraron modelos",
                        onChange: (opt) => this.aplicar("modelo", opt),
                    })}

                    <CargadorVersiones modeloId={values.modelo?.id}/>

                    {this.campo("version", "Versión", {
                        isDisabled: values.modelo?.id === undefined,
                        value: porId(filtros?.versiones, values.version?.id),
                        placeholder: values.modelo?.id === undefined ? "Elegí un modelo" : "Todas",
                        options: this.buscarMisVersiones(filtros?.versiones, values.modelo?.id),
                        noOptionsMessage: () => "No se encontraron versiones",
                        onChange: (opt) => this.aplicar("version", opt),
                    })}
                </fieldset>

                {/* Nuevo/usado, ubicacion y vendedor son del sitio general: en una tienda propia
                    hay un solo vendedor (y un solo rubro). */}
                {!enTienda && (
                    <fieldset className="filtros__grupo">
                        <legend className="filtros__titulo">Más filtros</legend>

                        {this.campo("condicion", "Condición", {
                            value: porId(CONDICIONES, values.condicion?.id),
                            placeholder: "Nuevos y usados",
                            getOptionLabel: (option) => option.nombre,
                            options: CONDICIONES,
                            isSearchable: false,
                            onChange: (opt) => this.aplicar("condicion", opt),
                        })}

                        {this.campo("provincia", "Provincia", {
                            value: porId(filtros?.provincias, values.provincia?.id),
                            placeholder: "Todas",
                            options: filtros?.provincias,
                            noOptionsMessage: () => "No se encontraron provincias",
                            onChange: (opt) => this.aplicar("provincia", opt),
                        })}

                        {this.campo("vendedor", "Vendedor", {
                            value: porId(filtros?.vendedores, values.vendedor?.id),
                            placeholder: "Todos",
                            getOptionLabel: nombreVendedor,
                            options: this.ordenarVendedores(filtros?.vendedores),
                            noOptionsMessage: () => "No se encontraron vendedores",
                            onChange: (opt) => this.aplicar("vendedor", opt),
                        })}
                    </fieldset>
                )}
            </div>
        );
    }
}

/**
 * Chips con los filtros aplicados, arriba del listado, cada uno con su X, y "Limpiar filtros"
 * cuando hay mas de uno.
 *
 * <p>Los nombres se buscan en los catalogos por id: los valores que vienen de la URL o del
 * buscador del header traen solo el id.
 *
 * @param ocultar campos que la pantalla fija por su cuenta y no se pueden sacar desde aca
 */
export const FiltrosActivos = ({values, filtros, setFieldValue, submitForm, ocultar = []}) => {
    const listas = {
        categoria: filtros?.categorias,
        tipoRepuesto: filtros?.tipoRepuestos,
        marca: filtros?.marcas,
        modelo: filtros?.modelos,
        version: filtros?.versiones,
        condicion: CONDICIONES,
        provincia: filtros?.provincias,
        vendedor: filtros?.vendedores,
    };

    const activos = CAMPOS
        .filter((c) => !ocultar.includes(c) && values?.[c]?.id !== undefined)
        .map((c) => {
            const it = porId(listas[c], values[c].id) || values[c];
            const nombre = c === "vendedor" ? nombreVendedor(it) : it.nombre;
            return {campo: c, nombre};
        })
        .filter((a) => a.nombre);

    if (activos.length === 0) return null;

    const quitar = (campos) => {
        campos.forEach((c) => {
            setFieldValue(c, undefined);
            (DEPENDIENTES[c] || []).forEach((dep) => setFieldValue(dep, undefined));
        });
        submitForm();
    };

    return (
        <ul className="filtros__activos" aria-label="Filtros aplicados">
            {activos.map((a) => (
                <li key={a.campo} className="filter__busqueda">
                    <span>{a.nombre}</span>
                    <button type="button" title={"Quitar " + a.nombre} onClick={() => quitar([a.campo])}>
                        <FontAwesomeIcon icon="fa-solid fa-xmark"/>
                    </button>
                </li>
            ))}
            {activos.length > 1 && (
                <li>
                    <button
                        type="button"
                        className="filtros__limpiar"
                        onClick={() => quitar(activos.map((a) => a.campo))}
                    >
                        Limpiar filtros
                    </button>
                </li>
            )}
        </ul>
    );
};

export default MenuFiltro;
